import { LuIcon, Menu, type MenuRootProps, type MenuVariantProps } from "@overmap-ai/blocks"
import type { FormIdentifier, Project, Stored, User } from "@overmap-ai/common"
import { type Field, FieldSchema } from "@overmap-ai/forms"
import { memo, type ReactElement, useCallback, useMemo } from "react"

import { CreatedByFilterDef } from "../../filtering/defs/CreatedByFilterDef"
import { IdentifierFilterDef } from "../../filtering/defs/IdentifierFilterDef"
import { ProjectFilterDef } from "../../filtering/defs/ProjectFilterDef"
import { SubmittedAtFilterDef } from "../../filtering/defs/SubmittedAtFilterDef"
import { getFilterDefOfField } from "../../filtering/defs/utils"
import { FilterManager } from "../../filtering/manager"
import { modelIconMapping } from "../../mappings/icons"
import type { FormSubmissionData } from "../../typings"
import { FieldGroup } from "../MenuGroups/FieldGroup"
import { IdentifierGroup } from "../MenuGroups/IdentifierGroup"

export interface FormSubmissionDataFilterMenuProps extends MenuRootProps, MenuVariantProps {
	children: ReactElement
	filterManager: FilterManager<FormSubmissionData>
	formIdentifiers: Stored<FormIdentifier>[]
	fieldSchema: FieldSchema
	users: User[]
	projects?: Project[]
}

export const FormSubmissionDataFilterMenu = memo<FormSubmissionDataFilterMenuProps>((props) => {
	const {
		children,
		variant,
		size,
		accentColor,
		filterManager,
		formIdentifiers,
		fieldSchema,
		users,
		projects,
		...rest
	} = props

	const handleSelectCreatedBy = useCallback(() => {
		filterManager.addFilter(new CreatedByFilterDef(users).createFilter())
	}, [filterManager, users])

	const handleSelectSubmittedAt = useCallback(() => {
		filterManager.addFilter(new SubmittedAtFilterDef().createFilter())
	}, [filterManager])

	const handleSelectProject = useCallback(() => {
		if (!projects) return
		filterManager.addFilter(new ProjectFilterDef(projects).createFilter())
	}, [filterManager, projects])

	const handleIdentifierSelect = useCallback(
		(identifier: Stored<FormIdentifier>) => {
			filterManager.addFilter(new IdentifierFilterDef(identifier).createFilter())
		},
		[filterManager],
	)

	const handleFieldSelect = useCallback(
		(field: Field) => {
			console.debug(field)
			filterManager.addFilter(getFilterDefOfField(field).createFilter())
		},
		[filterManager],
	)

	const supportedFields = useMemo(() => fieldSchema.getFields(), [fieldSchema])

	return (
		<Menu.Root {...rest}>
			<Menu.ClickTrigger>{children}</Menu.ClickTrigger>
			<Menu.Content variant={variant} size={size} accentColor={accentColor}>
				<Menu.Scroll>
					<Menu.Item onSelect={handleSelectCreatedBy}>
						<LuIcon icon={modelIconMapping.creator} />
						Created by
					</Menu.Item>
					<Menu.Item onSelect={handleSelectSubmittedAt}>
						<LuIcon icon={modelIconMapping.dueDate} />
						Submitted at
					</Menu.Item>

					{projects ? (
						<Menu.Item onSelect={handleSelectProject}>
							<LuIcon icon={modelIconMapping.project} />
							Project
						</Menu.Item>
					) : null}

					{formIdentifiers.length > 0 && (
						<>
							<Menu.Label>Identifiers</Menu.Label>
							<IdentifierGroup
								identifiers={formIdentifiers}
								onIdentifierSelect={handleIdentifierSelect}
							/>
						</>
					)}

					{supportedFields.length > 0 && (
						<>
							<Menu.Label>Fields</Menu.Label>
							<FieldGroup fields={supportedFields} onFieldSelect={handleFieldSelect} />
						</>
					)}
				</Menu.Scroll>
			</Menu.Content>
		</Menu.Root>
	)
})
FormSubmissionDataFilterMenu.displayName = "FormSubmissionFilterMenu"
